import React, { useState, useEffect } from 'react';
import { Box, Typography, Container } from '@mui/material';

interface YouTubeVideoProps {
  videoUrl: string;
  title?: string;
  autoPlay?: boolean;
  loop?: boolean;
  hideControls?: boolean;
  width?: string | number;
  height?: string | number;
}

/**
 * YouTubeVideo - A component for displaying YouTube videos on the international brand pages
 * with hidden YouTube branding and controls
 */
const YouTubeVideo: React.FC<YouTubeVideoProps> = ({
                                                     videoUrl,
                                                     title,
                                                     autoPlay = true,
                                                     loop = true,
                                                     hideControls = false,
                                                     width = '100%',
                                                     height = '600px',
                                                   }) => {
  const [videoId, setVideoId] = useState<string | null>(null);
  useEffect(() => {
    if (videoUrl) {
      const id = extractYouTubeId(videoUrl);
      setVideoId(id);
    }
  }, [videoUrl]);

  // Function to extract YouTube video ID from various URL formats
  const extractYouTubeId = (url: string): string | null => {
    try {
      // Handle youtube.com/watch?v= format
      let match = url.match(/youtube\.com\/watch\?v=([^&]+)/);
      if (match) return match[1];

      // Handle youtu.be/ format
      match = url.match(/youtu\.be\/([^?]+)/);
      if (match) return match[1];

      // Handle youtube.com/embed/ format
      match = url.match(/youtube\.com\/embed\/([^?]+)/);
      if (match) return match[1];

      // If no matching format, return null
      return null;
    } catch (error) {
      console.error("Error extracting YouTube ID:", error);
      return null;
    }
  };

  // Build YouTube embed URL with parameters
  const buildEmbedUrl = (): string => {
    if (!videoId) return '';

    let url = `https://www.youtube.com/embed/${videoId}?`;

    // Add parameters
    const params = new URLSearchParams();

    if (loop) params.append('loop', '1');
    if (loop) params.append('playlist', videoId); // Required for looping
    if (hideControls) params.append('controls', '0');
    if (autoPlay) params.append('autoplay', '1');

    // Add additional parameters for better embedding
    params.append('mute', '1'); // Muted by default (autoplay requires mute)
    params.append('playsinline', '1');
    params.append('modestbranding', '1'); // Minimal YouTube branding

    // If no controls, also disable related videos and info
    if (hideControls) {
      params.append('rel', '0');
      params.append('showinfo', '0');
      params.append('iv_load_policy', '3'); // Disable annotations
    }

    // Additional parameters to minimize branding
    params.append('fs', '0'); // Disable fullscreen button
    params.append('disablekb', '1'); // Disable keyboard controls

    return url + params.toString();
  };

  return (
    <Container maxWidth="lg">
      {title && (
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
          {title}
        </Typography>
      )}

      {videoId ? (
        <Box
          sx={{
            position: 'relative',
            width: width,
            height: height,
            overflow: 'hidden',
            borderRadius: '4px',
            bgcolor: 'black',
          }}
        >
          {/* YouTube iframe - positioned to crop out top and bottom branding */}
          <Box
            component="iframe"
            src={buildEmbedUrl()}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={title || "YouTube Video"}
            sx={{
              position: 'absolute',
              width: '100%',
              height: 'calc(100% + 120px)', // Make taller to crop bottom controls
              top: '-60px', // Shift up to crop top YouTube bar
              left: 0,
              border: 'none',
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: width,
            height: height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.05)',
            borderRadius: '4px'
          }}
        >
          <Typography color="text.secondary">
            {videoUrl ? 'Invalid YouTube URL' : 'No video provided'}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default YouTubeVideo;